import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateBookingCommanWay, UpdateCommanWay } from "API/commonApi";
import axios, { AxiosResponse } from "axios";
import { InputTable } from "components/DataTable/InputTable";
import { useFormik } from "formik";
import { debounce } from "lodash";
import moment from "moment";
import { any } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import UseToast from "utils/AfToast";
import { constantValues, roles } from "utils/constant";
import handleWheel from "utils/handleOnWheel";
import { AfDataType as ADT } from "utils/TableColumnData";
import { number, object, string } from "yup";
import { BookingInputTable } from "./BookingInputTable";
import Swal from "sweetalert2";
import Spinner from "utils/Spinner";
import { request } from "config/request";
import MultiImageUploadModal from "./MultiImageUploadModal";
import { AwbLabel } from "components/PrintLabels/AwbLabel";
import { Button } from "@heroui/react";

// Modern select styles
const bookingSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
    minHeight: "28px",
    fontSize: "12px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    "&:hover": { borderColor: "#cbd5e1" },
    backgroundColor: "white",
    transition: "all 0.15s ease",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, height: "28px" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "4px" }),
  clearIndicator: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    "&:active": { backgroundColor: "#e0e7ff" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  singleValue: (base: any) => ({ ...base, color: "#334155" }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
};

// MInput Component
const MInput = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  onBlurExtra,
  type = "text",
  disabled = false,
  className = "",
  placeholder = "",
  min,
  max,
}: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <input
      type={type}
      className={`flex-1 min-w-0 px-2 py-[3px] rounded-lg border text-[13px] outline-none transition-all ${
        disabled
          ? "border-slate-200 bg-slate-100 text-slate-500"
          : "border-slate-200 bg-white focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
      }`}
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={(e) => {
        onBlur && onBlur(e);
        onBlurExtra && onBlurExtra(e);
      }}
      onWheel={handleWheel}
      placeholder={placeholder || label}
      disabled={disabled}
      min={min}
      max={max}
    />
  </div>
);

// MSelect Component
const MSelect = ({ label, name, options, value, onChange, onBlur, disabled = false, className = "" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <Select
      className="flex-1 min-w-0"
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      isDisabled={disabled}
      styles={bookingSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  </div>
);

// MCheckbox Component
const MCheckbox = ({ label, name, value, onChange, disabled = false }: any) => (
  <label className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-all cursor-pointer text-[11px] font-medium text-slate-600">
    <input
      type="checkbox"
      name={name}
      checked={value == 1 || value === true}
      onChange={onChange}
      disabled={disabled}
      className="w-3.5 h-3.5 rounded border-slate-300 text-primary2 focus:ring-primary2/20"
    />
    {label}
  </label>
);

type OptionType = {
  value: number;
  label: string;
};

interface BookingState {
  mode?: string;
  data?: { [key: string]: any }; // Adjust the type based on your data structure
  // Add other properties if needed
}
declare global {
  interface Window {
    SEND_WAPP: (num: any, link: any, msg: any) => void;
  }
}

const AddBooking: React.FC = ({}) => {
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;

  let isDisabled = false;
  const navigate = useNavigate();
  const { state } = useLocation();
  const { mode = "add", data = {} } = (state || {}) as BookingState; // Check for existence and type assertion

  const [modalOpen, setModalOpen] = useState(false);
  const [aiResponse, setAiReponse] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const [isBranchSet, setIsBranchSet] = useState<any>();
  const [newTableData, setNewTableData] = useState<any>([]);
  const newTableDataRef = useRef(newTableData);
  const productListRef = useRef<any[]>([]);
  const [nestedTables, setNestedTables] = useState<any>({});
  const [pincodeSection, setPincodeSection] = useState<any>("");
  const [userDetails, setUserDetails] = useState<any>({});
  const [subbranchOpt, setSubBranchOpt] = useState<any>();

  const [isDateDisabled, setIsDateDisabled] = useState<boolean>(false);
  const [bookingData, setBookingData] = useState<any>();

  const [commentVal, setCommentVal] = useState<any>("");

  const [isLabelOpen, setIsLabelOpen] = useState(-1);
  const [savedAwbData, setSavedAwbData] = useState<any>([]);

  const [customerWallet, setCustomerWallet] = useState<any>("N/A");
  const [pickupProductInfo, setPickupProductInfo] = useState<any>(null);
  const [mainAwb, setMainAwb] = useState<string>("");
  const [billingDiffFromShipping, setBillingDiffFromShipping] = useState(false);
  const [gstSearching, setGstSearching] = useState(false);

  const isValidGSTIN = (gstin: string) => {
    if (!gstin || gstin.length !== 15) return false;
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstin.toUpperCase());
  };

  const handleGstSearch = (gstin: string, setFV: any) => {
    const upper = gstin.toUpperCase().trim();
    if (!isValidGSTIN(upper)) {
      if (upper.length > 0) UseToast("Invalid GSTIN format", "error");
      return;
    }
    setGstSearching(true);
    GetCommanWay(`/admin/gst/search/${upper}`, {}, {})
      .then((data: any) => {
        if (data.status === "SUCCESS" && data.data) {
          const d = data.data;
          const addrParts = [d.AddrFlno, d.AddrBno, d.AddrBnm, d.AddrSt, d.AddrLoc].filter(Boolean).map((s: string) => s.trim()).filter(Boolean);
          setFV("billing_gst_no", d.Gstin || upper);
          setFV("billing_name", d.LegalName || d.TradeName || "");
          setFV("billing_address", addrParts.join(", "));
          setFV("billing_pincode", d.AddrPncd ? String(d.AddrPncd) : "");
          setFV("billing_city", d.AddrLoc ? d.AddrLoc.trim() : "");
          // Trigger pincode lookup for state
          if (d.AddrPncd && String(d.AddrPncd).length === 6) {
            setPincodeSection("billing");
            mutatePincode(String(d.AddrPncd));
          }
          UseToast("GST details loaded", "success");
        } else {
          UseToast(data.message || "GST not found", "error");
        }
      })
      .catch((err: any) => {
        UseToast(typeof err === "string" ? err : err.message || "GST search failed", "error");
      })
      .finally(() => setGstSearching(false));
  };

  const SEND_WAPP = (num: any, link: any, msg: any) => {
    axios.request({
      url: BASE_URL + `/admin/send-wapp`,
      method: "POST",
      data: {
        mobile: num,
        pdf: link,
        msg: msg,
      },
    });
    alert("Whatsapp Sent Successfully!");
  };
  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      console.log("userD.state", userD.state.user);
      if (userD.state.user.roleName !== roles.SUPER_ADMIN && userD.state.user.roleName !== roles.BRANCH_ADMIN) {
        setSubBranchOpt(userD.state.user.branch_name);

        mutateUserBranch(userD.state.user.subbranch);
      }

      if (userD.state.user.roleName !== roles.SUPER_ADMIN) {
        setIsDateDisabled(true);
      }

      if (userD.state.user?.roleName == "CUSTOMER") {
        setFieldValue("shipper_pincode", userD.state.user.pincode);
        setPincodeSection("shipper");
        setTimeout((x) => {
          mutatePincode(userD.state.user.pincode);
        }, 200);
        setFieldValue("shipper_gst_no", userD.state.user.gst);
        setFieldValue("shipper_pan", userD.state.user.pan);
        setFieldValue("shipper_address", userD.state.user.address);
        setFieldValue("shipper_company", userD.state.user.sh);

        //mutatePincode(userD.state.user.pincode);
      }
    }
    window.SEND_WAPP = SEND_WAPP;
    (window as any).setIsLabelOpen = setIsLabelOpen;
  }, []);
  useEffect(() => {
    newTableDataRef.current = newTableData;
  }, [newTableData]);

  const {
    mutate: mutateUserBranch,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((subbranch) => ListCommanWay("/admin/subbranch/list", { id: subbranch }, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        console.log(data.data.data);

        if (mode == "edit") {
        } else {
          //// setFieldValue("shipper_pincode", data.data.data[0].pincode);
          //// setPincodeSection("shipper");
          /*setTimeout((x) => {
            mutatePincode(data.data.data[0].pincode);
          }, 200);*/
        }
      }
    },
    onError: (data: any) => {},
  });

  const {
    mutate: priceCheckMutate,
    isLoading: isloadingPrice,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        Swal.fire({
          title: "Are you sure!",
          text: data.data == "NA" ? "---" : "Price: " + (data.data.price + data.data.tax).toFixed(0),
          icon: "info",
          confirmButtonText: "Do Confirm Booking",
          showCancelButton: true,
        }).then((x) => {
          if (x.isConfirmed) {
            mutate({ url: "/admin/booking/create", data: bookingData });
          }
        });
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      try {
        if (data.indexOf("Invalid Pincode:") != -1) {
          let pins = data.replaceAll("Invalid Pincode:", "");
          pins = pins.split(",");
          let srNo: any[] = [];
          bookingData.awb_data.forEach((x, i) => {
            if (!pins.includes(x.pincode)) {
              srNo.push(i + 1);
            }
          });
          UseToast("Invalid Pincodes at Sr no.: " + srNo.join(", "), "error");
        } else {
          typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
        }
      } catch (c) {
        console.log(c);
      }
    },
  });

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        //UseToast("Data Added Successfully", "success");
        resetForm();
        setNewTableData([]);
        setAiReponse([]);
        //successNavigate();

        console.log(data.data);
        let labelData: any[] = [];
        data.data.awb_nos.forEach((x, i) => {
          labelData.push({
            ...x,
            _assign_to_booking: data.data.dataValues || data.data.dataValues,
          });
        });
        setSavedAwbData(labelData);

        const firstAwb = data.data.awb_nos[0]?.awb_no || "";

        let msg = `Dear Patron,%0A%0AWe welcome you to Airfield Express and are happy to be of service to you. You can track your shipment and delivery status by clicking on the following link: %0A%0Ahttps://ats.airfield.express/public-tracking?awb=${firstAwb} %0A%0AWe thank you for choosing our service and being part of Green Earth Initiative by accepting  this digital receipt and refuse to use paper, saving trees. Thank you for being an important part of Airfield Express Pvt. Ltd.For any further and future query / feedback / support, please contact us on our National Helpline no: 922 722 3131 or write to us at cs@airfield.co.in.%0A%0AVisit us at https://www.airfield.co.in for any information.%0A%0AHave a Safe Healthy and Peaceful Day Ahead.`;

        let awbBtns = '<br/><br/><hr/><h2 class="mt-6 mb-2">Downloan AWB Slip</h2>';
        try {
          data.data?.awb_nos.forEach((x, i) => {
            //awbBtns += `<a class="text-primary underline mx-2" href='/awb/slip?awb=${x.awb_no}' target="_blank">${x.awb_no}</a>`;
            awbBtns += `<button class="text-primary underline mx-2" onclick="setIsLabelOpen(${i})">${x.awb_no}</button>`;
          });
        } catch (c) {}

        Swal.fire({
          title: "Booking Successful",
          text: data.data?.pdfPath,
          html: data.data?.pdfPath
            ? `
            <input id="shipper_mobile_wapp" class="border border-black-900 focus:border focus:border-black-900 rounded-md" type="text" value="${values.shipper_mobile}"/>
          <a onClick="window.SEND_WAPP('${values.shipper_mobile}', '${BASE_URL}${data.data?.pdfPath}', '${firstAwb}')" class="text-primary underline cursor-pointer">Send Whatsapp</a><br><br>
          <a class="text-primary underline" href='${BASE_URL}${data.data?.pdfPath}' target="_blank">Download Invoice</a>${awbBtns}`
            : `<input id="shipper_mobile_wapp" class="border border-black-900 focus:border focus:border-black-900 rounded-md" type="text" value="${values.shipper_mobile}"/>
          <a onClick="window.SEND_WAPP('${values.shipper_mobile}', '', '${firstAwb}')" class="text-primary underline cursor-pointer">Send Whatsapp</a><br><br>` + awbBtns,
          icon: "info",
          showConfirmButton: false,
          showCancelButton: true,
          cancelButtonText: "OK",
        }).then((x) => {});
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: updateBookingMutate,
    isLoading: isupdateBookingloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => UpdateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        UseToast("Data Updated Successfully", "success");
        //navigate("/report/booking");
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleQuantityChange = (e, doLoadPickup) => {
    let quantity = parseInt(e.target.value);

    console.log("quantitychange", doLoadPickup);
    if (quantity > 99) {
      setFieldValue("quantity", 99);
      return;
    }
    setNewTableData((prevData) => {
      const columnKeys = columnData.map((d) => d.key);

      let newData: any[] = [];
      let startIdx = 0;

      if (doLoadPickup) {
      } else {
        if (quantity <= prevData.length) {
          // If quantity is less than or equal to current data length, slice the array
          return prevData.slice(0, quantity);
        }

        startIdx = prevData.length;
        newData = [...prevData];
      }

      // If quantity increases, keep existing rows and add new ones
      ////const newData = [...prevData];
      for (let index = startIdx; index < quantity; index++) {
        const obj = {};
        for (let j = 0; j < columnKeys.length; j++) {
          obj[columnKeys[j]] = columnData.find((column) => column.key === columnKeys[j])?.initialValue;
        }
        obj["id"] = newData[newData.length - 1]?.id ? newData[newData.length - 1].id + 1 : 1;
        obj["destination"] = "";

        if (doLoadPickup && doLoadPickup.length) {
          obj["pincode"] = doLoadPickup[0].consignee_pincode;
          obj["destination"] = doLoadPickup[0].consignee_city;
          obj["contact_no"] = doLoadPickup[0].consignee_mobile;
          obj["consignee"] = doLoadPickup[0].consignee_cgnee;
          obj["address"] = doLoadPickup[0].consignee_address;
          obj["ten_30"] = doLoadPickup[0].ten_30;
          obj["dox_or_non_dox"] = doLoadPickup[0].type == "DOX" ? true : false;
          obj["holiday"] = doLoadPickup[0].holiday;

          obj["diceWeight"] = parseFloat(doLoadPickup[0].dice_weight);
          obj["dgRate"] = parseFloat(doLoadPickup[0].dg_charge);

          obj["extraPkgRate"] = parseFloat(doLoadPickup[0].pkg_charge);
          obj["pkg1Rate"] = parseFloat(doLoadPickup[0].pkg1Charge);
          obj["pkg2Rate"] = parseFloat(doLoadPickup[0].pkg2Charge);
          obj["pkg3Rate"] = parseFloat(doLoadPickup[0].pkg3Charge);

          obj["odaRate"] = parseFloat(doLoadPickup[0].oda_charge);

          // Handle pkgData JSON for cube sizes
          if (doLoadPickup[0].pkgData) {
            let pkgData = doLoadPickup[0].pkgData;
            if (typeof pkgData === "string") {
              try {
                pkgData = JSON.parse(pkgData);
              } catch {
                pkgData = {};
              }
            }
            // Auto-check the cube based on boxSize value
            if (pkgData?.boxSize) {
              obj["pkgData"] = { [pkgData.boxSize]: "1" };
            }
          }
          
          // Set product code from pickup data for auto-generate
          if (doLoadPickup[0].product_name) {
            let pCode = doLoadPickup[0].product_code;
            if (!pCode) {
              const match = productListRef.current.find((p: any) => p.label === doLoadPickup[0].product_name);
              pCode = match?.value || "";
            }
            obj["autoGen_product"] = pCode;
          }
        }

        newData.push(obj);
      }
      return newData;
    });
  };

  const { handleSubmit, handleBlur, values, handleChange, isSubmitting, setSubmitting, setFieldValue, resetForm } = useFormik({
    validationSchema: object().shape({}),
    initialValues: {
      date: moment().format("YYYY-MM-DD"),
      quantity: 0,
      type: "",
      pickup_code: "",
      customer_code: "",
      weight: 0,
      ten_30: { value: 0, label: "No" },
      dox_or_non_dox: { value: 0 },
      project_code: "",
      product_name: { value: 0, label: "" },
      holiday: { value: 0 },
      autoGen: 0,
      remarks: "",
      ewayBillNo: "",

      customer_pincode: "",
      customer_mobile: "",
      customer_sh: "",
      customer_city: "",
      customer_state: "",
      customer_country: "",
      customer_address: "",
      consignee_pincode: "",
      consignee_mobile: "",
      consignee_cgnee: "",
      consignee_city: "",
      consignee_state: "",
      consignee_country: "",
      consignee_address: "",
      consignee_company: "",

      shipper_pincode: "",
      shipper_mobile: "",
      shipper_sh: "",
      shipper_city: "",
      shipper_state: "",
      shipper_country: "",
      shipper_pan: "",
      shipper_gst_no: "",
      shipper_company: "",
      shipper_address: "",
      change_shipper: false,

      billing_name: "",
      billing_address: "",
      billing_city: "",
      billing_state: "",
      billing_pincode: "",
      billing_country: "India",
      billing_gst_no: "",
      billing_mobile: "",

      oda_charge: 0,
      pkgRate: 0,
      d_ice_charge: 0,
      pkg1Rate: 0,
      pkg2Rate: 0,
      pkg3Rate: 0,
      diceWeight: 0,
      dgRate: 0,

      branch_id: { value: 0, label: "" },
      subbranch: { value: 0, label: "select branch" },
    },
    onSubmit: async (values, { resetForm }) => {
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);

      if (mode && mode === "edit") {
        if (commentVal == "") {
          alert("Enter Comment");
          return;
        }
        //updateAwb({ data: newTableData, bookingId: data.id });
        let req: any = {};
        req.weight = values.weight.toString();
        req.comment = commentVal;

        req.shipper_sh = values.shipper_sh;
        req.shipper_pincode = values.shipper_pincode;
        req.shipper_address = values.shipper_address;
        req.shipper_mobile = values.shipper_mobile;
        req.shipper_pan = values.shipper_pan;
        req.shipper_gst_no = values.shipper_gst_no;
        req.shipper_company = values.shipper_company;
        req.shipper_city = values.shipper_city;
        req.shipper_state = values.shipper_state;
        req.date = values.date; ///moment().format("YYYY-MM-DD"),
        req.ewayBillNo = values.ewayBillNo;

        // Include billing data if GST billing is enabled
        if (billingDiffFromShipping) {
          req.billing_name = values.billing_name;
          req.billing_address = values.billing_address;
          req.billing_city = values.billing_city;
          req.billing_state = values.billing_state;
          req.billing_pincode = values.billing_pincode;
          req.billing_country = values.billing_country;
          req.billing_gst_no = values.billing_gst_no;
          req.billing_mobile = values.billing_mobile ? Number(String(values.billing_mobile).replaceAll(" ", "")) : "";
          req.customer_code = "";
        }

        req["awb_data"] = newTableData;

        // Re-build customFields for AHD48277 on edit (avoid double-encoding)
        if (values.customer_code === "AHD48277") {
          req["awb_data"].forEach((a: any) => {
            const cf: any = {};
            if (mainAwb) cf.mainAwb = mainAwb;
            if (a.codMaxDays) cf.codMaxDays = a.codMaxDays;
            a.customFields = Object.keys(cf).length ? cf : null;
          });
        }

        console.log(req);
        updateBookingMutate({
          url: "/admin/booking/update/" + data.id,
          data: req,
        });
        return;
      }
      let req: any = { ...values };
      req.type = req.type;
      req.ten_30 = req.ten_30 ? req.ten_30.value : undefined;
      req.product_name = req.product_name ? req.product_name.value : undefined;
      req.holiday = req.holiday === 1 ? true : false;

      req.weight = parseFloat(req.weight).toString();
      req.shipper_mobile = Number((req.shipper_mobile || "").replaceAll(" ", ""));
      req.consignee_mobile = Number((req.consignee_mobile || "").replaceAll(" ", ""));
      req.change_shipper = req.change_shipper.toString();
      if (req.customer_code == "0") {
        req.customer_code = "";
      }

      // If GST Billing is checked, clear customer data and keep billing fields
      if (billingDiffFromShipping) {
        req.customer_code = "";
        req.customer_pincode = "";
        req.customer_mobile = 0;
        req.customer_sh = "";
        req.customer_city = "";
        req.customer_state = "";
        req.customer_country = "";
        req.customer_address = "";
        req.billing_mobile = req.billing_mobile ? Number(String(req.billing_mobile).replaceAll(" ", "")) : "";
      } else {
        req.customer_mobile = Number((req.customer_mobile || "").replaceAll(" ", ""));
        // No separate billing, clear billing fields
        delete req.billing_name;
        delete req.billing_address;
        delete req.billing_city;
        delete req.billing_state;
        delete req.billing_pincode;
        delete req.billing_country;
        delete req.billing_gst_no;
        delete req.billing_mobile;
      }

      req.oda_charge = parseFloat(req.oda_charge);
      req.pkgRate = parseFloat(req.pkgRate);
      req.diceRate = parseFloat(req.diceRate);
      req.pkg1Charge = parseFloat(req.pkg1Charge || 0);
      req.pkg2Charge = parseFloat(req.pkg2Charge || 0);
      req.pkg3Charge = parseFloat(req.pkg3Charge || 0);
      userDetails?.roleName !== roles.SUPER_ADMIN && userDetails?.roleName !== roles.BRANCH_ADMIN
        ? (req.subbranch = userDetails.subbranch)
        : (req.subbranch = req.subbranch ? req.subbranch.value : null);

      let finalAwbData: any[] = [];
      let awbError = "";
      newTableData.forEach((a) => {
        // Skip completely blank rows (no awb, no consignee, no contact, no weight, no pincode)
        const isBlankRow = !a.awb_no && !a.consignee && !a.contact_no && !a.weight && !a.pincode && (!req.autoGen || !a.autoGen_product);
        if (isBlankRow) return;

        if (a.awb_no) {
          a.prefix = a.awb_no[0]?.toUpperCase();

          if (a.prefix == "S" && a.awb_no.length != 12) {
            awbError = "Airway bill: " + a.awb_no + " is Wrong !";
          } else if (a.prefix != "S" && a.awb_no.length != 13) {
            awbError = "Airway bill: " + a.awb_no + " is Wrong !";
          }

          // Validate AWB prefix for P/U type with pickup product
          if (values.type === "PU" && pickupProductInfo?.product_code) {
            const expectedPrefix = pickupProductInfo.product_code.toUpperCase();
            if (a.prefix !== expectedPrefix) {
              awbError = `AWB ${a.awb_no} must start with '${expectedPrefix}' for product ${pickupProductInfo.product_name}`;
            }
          }

          a.awb_no = a.awb_no.toUpperCase();

          finalAwbData.push({ ...a });
        } else if (req.autoGen) {
          if (a.autoGen_product) {
            // Validate product for P/U type with pickup product
            if (values.type === "PU" && pickupProductInfo?.product_code) {
              const expectedProductCode = pickupProductInfo.product_code.toUpperCase();
              if (a.autoGen_product.toUpperCase() !== expectedProductCode) {
                awbError = `Product must be ${pickupProductInfo.product_name} for P/U type booking`;
              }
            }
            a.prefix = a.autoGen_product?.toUpperCase();
          } else {
            awbError = "Please Select Product";
          }
          finalAwbData.push({ ...a });
        }
      });

      // Attach customFields for AHD48277
      if (values.customer_code === "AHD48277") {
        finalAwbData.forEach((a) => {
          const cf: any = {};
          if (mainAwb) cf.mainAwb = mainAwb;
          if (a.codMaxDays) cf.codMaxDays = a.codMaxDays;
          if (Object.keys(cf).length) a.customFields = cf;
        });
      }

      if (finalAwbData.length == 0) {
        alert("Missing Airwaybill info");
        return;
      }
      if (awbError) {
        alert(awbError);
        return;
      }
      req["awb_data"] = finalAwbData;
      delete req.id;
      delete req?.createdAt;
      constantValues.defaultFields.forEach((d) => delete req[d]);

      //return;
      let totalWeight = 0;
      finalAwbData.forEach((x) => {
        if (Number(x.weight) == 0) {
          awbError = "Weight is 0 for AWB: " + x.awb_no;
        }
        if (x.piece.length == 0) {
          if (x.pincode && x.pincode.length != 6) {
            awbError = "Pincode is missing for AWB: " + x.awb_no;
          }

          if (x.consignee == "" || x.address == "") {
            awbError = "Consignee Info is missing for AWB: " + x.awb_no;
          }
          totalWeight += Number(x.weight);
        } else {
          let pieceTotalW = 0;
          x.piece.forEach((p) => {
            pieceTotalW += Number(p.weight);
            //totalWeight += Number(p.weight);
          });
          if (Number(x.weight) != pieceTotalW) {
            awbError = "Piece Total is not Matching with Total Weight for AWB: " + x.awb_no;
          }
          totalWeight += pieceTotalW;
        }
      });

      if (awbError) {
        alert(awbError);
        return;
      }

      let awbs: any[] = [];
      req.awb_data.forEach((a) => {
        awbs.push(a.awb_no);
      });

      if (!req.autoGen) {
        if (new Set(awbs).size !== awbs.length) {
          alert("Duplicate AWB !");
          return;
        }
      }

      if (totalWeight == 0) {
        alert("Total Weight is wrong!");
        return;
      }
      if (req.shipper_city == "" || req.shipper_pincode == "" || req.shipper_sh == "" || req.shipper_address == "") {
        return alert("Shipper Info is missing");
      }
      req.quantity = req.awb_data.length;
      req.weight = totalWeight.toString();
      priceCheckMutate({ url: "/admin/booking/check-price", data: req });
      setBookingData(req);
    },
  });

  const columnData = [
    {
      label: "NO.",
      key: "id",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      edit: false,
      add: true,
      filter: true,
      sort: true,
    },
    {
      label: "Pieces",
      key: "piece",
      type: "PIECE",
      initialValue: [],
      view: true,
      add: true,
      edit: false,
      filter: false,
      sort: true,
    },
    ...(!values.autoGen
      ? [
          {
            label: "AWB NO.",
            key: "awb_no",
            type: ADT.TEXT,
            initialValue: "",
            view: true,
            edit: false,
            add: true,
            filter: true,
            sort: true,
            css: "w-32",
          },
        ]
      : [
          {
            label: "Product",
            key: "autoGen_product",
            type: ADT.DB,
            db: "product",
            db_col: "product_name",
            keyVal: "product_code",
            initialValue: values.type === "PU" && pickupProductInfo?.product_code ? pickupProductInfo.product_code : "",
            view: true,
            add: true,
            edit: true,
            filter: true,
            sort: true,
            css: "w-32",
            // Restrict to pickup product when P/U type is selected
            filterOptions: values.type === "PU" && pickupProductInfo?.product_code ? pickupProductInfo.product_code : null,
          },
        ]),
    {
      label: "Weight",
      key: "weight",
      type: ADT.NUMBER,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-[55px]",
    },
    {
      label: "Pin / Dest",
      key: "pincode",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-[60px]",
      hasSubField: "destination",
    },

    {
      label: "Contact No",
      key: "contact_no",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },
    {
      label: "Company",
      key: "consignee_company",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },
    {
      label: "Consignee",
      key: "consignee",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },
    {
      label: "Address",
      key: "address",
      type: ADT.TEXT,
      initialValue: "",
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
    },
    {
      label: "Dox/Non Dox",
      key: "dox_or_non_dox",
      type: ADT.SELECT,
      options: [
        { value: true, label: "DOX" },
        { value: false, label: "NON DOX" },
      ],
      initialValue: false,
      view: true,
      add: true,
      edit: true,
      filter: true,
      sort: true,
      css: "w-24",
    },
    ...(values.customer_code == "" || true
      ? [
          {
            label: "extra",
            key: "extraAmt",
            type: "extraAmt",
            initialValue: [],
            view: true,
            add: true,
            edit: false,
            filter: false,
            sort: true,
          },
        ]
      : []),

    ...(values.customer_code === "AHD48277"
      ? [
          {
            label: "Value",
            key: "commercialValue",
            type: ADT.NUMBER,
            initialValue: "",
            view: true,
            add: true,
            edit: true,
            filter: false,
            sort: false,
            css: "w-[70px]",
          },
          {
            label: "FOD",
            key: "isFod",
            type: ADT.CHECKBOX,
            initialValue: false,
            view: true,
            add: true,
            edit: true,
            filter: false,
            sort: false,
          },
          {
            label: "COD",
            key: "isCod",
            type: ADT.CHECKBOX,
            initialValue: false,
            view: true,
            add: true,
            edit: true,
            filter: false,
            sort: false,
          },
        ]
      : []),

    ...(userDetails.roleName === roles.SUPER_ADMIN
      ? [
          {
            label: "disc.(%)",
            key: "discount",
            type: ADT.NUMBER,
            initialValue: "",
            view: true,
            add: true,
            edit: true,
            filter: true,
            sort: true,
            css: "w-[60px]",
          },
        ]
      : []),
  ];

  const {
    mutate: mutatePincode,
    isLoading: isPincodeloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pincode) => GetCommanWay("/admin/address/" + pincode, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        Object.keys(data.data).map((d) => {
          setFieldValue(pincodeSection + "_" + d, data.data[d]);
        });
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");

      setFieldValue(pincodeSection + "_city", "");
      setFieldValue(pincodeSection + "_country", "");
      setFieldValue(pincodeSection + "_state", "");
      return { city: "", country: "", pincode: "", region: "", state: "" };
    },
  });

  const {
    mutate: mutatePickup,
    isLoading: isPickuploading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pickup_code) => ListCommanWay("/admin/pickup/list", { pickup_code }, {}), {
    onSuccess: async (data: any) => {
      if (data.status === "SUCCESS") {
        if (data.data?.data?.length > 0) {
          let isBooked = false;

          try {
            let booked = await ListCommanWay(
              "/admin/booking/list",
              {
                qry: `pickup_code = '${values.pickup_code}'`,
              },
              {}
            );
            if (booked.data.data.length > 0) {
              isBooked = true;
            }
          } catch (c) {}

          if (isBooked) {
            let pickupTime = moment(data.data.data[0].pickup_date_time || data.data.data[0].date, "DD-MM-YYYY HH:mm:ss");
            if (!data.data.data[0].pickup_date_time || data.data.data[0].pickup_date_time == "Invalid Date") {
              pickupTime = moment(data.data.data[0].date, "YYYY-MM-DD");
            }
            let diff = moment().diff(pickupTime, "h");
            if (diff > 55) {
              Swal.fire({
                title: "Reference Code is expired !",
                icon: "error",
              });
              setFieldValue("pickup_code", "");
              return;
            }
          }
        }

        // Store pickup product info for validation
        if (data.data.data[0]?.product_name) {
          let pCode = data.data.data[0].product_code;
          if (!pCode) {
            const match = productListRef.current.find((p: any) => p.label === data.data.data[0].product_name);
            pCode = match?.value || "";
          }
          setPickupProductInfo({
            product_name: data.data.data[0].product_name,
            product_code: pCode
          });
        } else {
          setPickupProductInfo(null);
        }

        data.data.data.map((d) => {
          try {
            let pickupTime = moment(d.pickup_date_time || d.date, "DD-MM-YYYY HH:mm:ss");
            if (!d.pickup_date_time || d.pickup_date_time == "Invalid Date") {
              pickupTime = moment(data.data.data[0].date, "YYYY-MM-DD");
            }
            d.date = pickupTime.format("YYYY-MM-DD");
          } catch (c) {
            d.date = moment(d.date).format("YYYY-MM-DD");
          }

          Object.keys(d).map((obj) => {
            if (!["type", "createdAt"].includes(obj)) {
              setFieldValue(obj, d[obj]);
            }

            if (obj === "quantity" && Number(d[obj]) >= 0) {
              fetchDBInputData().then((value) => {
                value && handleQuantityChange({ target: { value: d[obj] } }, data.data.data);
              });
            }
          });
        });
        return data.data[0];
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateCustomer,
    isLoading: isCustomerloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((code) => GetCommanWay("/admin/customer/get/" + code, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        Object.keys(values).map((d) => {
          if (d.split("customer_")[1]) {
            if (d != "customer_mobile") {
              setFieldValue("customer_" + d.split("customer_")[1], data.data[d.split("customer_")[1]]);
            }
          }
        });
        //values.customer_mobile = data.data.officePhone;
        //values.shipper_mobile = data.data.officePhone;
        setFieldValue("shipper_pincode", data.data.pincode);
        setFieldValue("shipper_sh", data.data.sh);
        setFieldValue("shipper_city", data.data.city);
        setFieldValue("shipper_state", data.data.state);
        setFieldValue("shipper_country", data.data.country);
        setFieldValue("shipper_pan", data.data.pan);
        setFieldValue("shipper_gst_no", data.data.gst);
        setFieldValue("shipper_address", data.data.address);

        setCustomerWallet(data.data.ballance || "0");

        return data.data;
      }
    },
    onError: (data: any) => {
      setCustomerWallet("N/A");
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const { mutate: mutateSavedMobile }: any = useMutation((code) => ListCommanWay("/admin/savedcustomer/list/", { mobile: code }, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        if (data.data?.data?.length > 0) {
          setFieldValue("shipper_sh", data.data.data[0].name);
          setFieldValue("shipper_address", data.data.data[0].address);
          setFieldValue("shipper_pan", data.data.data[0].pan || "");
          setFieldValue("shipper_gst_no", data.data.data[0].gst || "");
          setFieldValue("shipper_company", data.data.data[0].shipper_company || "");
        }
        return data.data;
      }
    },
    onError: (data: any) => {
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateBooking,
    isLoading: isBookingloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((code) => GetCommanWay("/admin/booking/" + code, {}, {}), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        console.log("data.data", data.data);

        data.data.awb_data.forEach((a) => {
          try {
            a.piece = JSON.parse(a.innerItemsJson);

            let basicAmt =
              Number(a.base_value) -
              (Number(a.holidayRate) || 0) -
              (Number(a.ten_30Rate) || 0) -
              (Number(a.fuelRate) || 0) -
              (Number(a.abRate) || 0) -
              (Number(a.chcRate) || 0) -
              (Number(a.diceRate) || 0) -
              (Number(a.dgRate) || 0) -
              (Number(a.pkgRate) || 0) -
              (Number(a.odaRate) || 0);
            a.chcPer = ((a.chcRate / basicAmt) * 100).toFixed(2);
            if (a.realWeight) {
              a.weight = a.realWeight;
            }
          } catch (c) {
            a.piece = [];
          }
        });

        // Extract mainAwb and codMaxDays from customFields
        try {
          let cf = data.data.awb_data[0]?.customFields;
          if (cf) {
            while (typeof cf === "string") { cf = JSON.parse(cf); }
            if (cf.mainAwb) setMainAwb(cf.mainAwb);
          }
        } catch {}

        // Extract codMaxDays from customFields for each AWB row
        data.data.awb_data.forEach((a) => {
          try {
            let cf = a.customFields;
            if (cf) {
              while (typeof cf === "string") { cf = JSON.parse(cf); }
              if (cf.codMaxDays) a.codMaxDays = cf.codMaxDays;
            }
          } catch {}
        });

        setNewTableData(data.data.awb_data);
        return data.data;
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const fetchDBInputData = async () => {
    let proms: any[] = [];
    console.log(JSON.stringify(nestedTables));
    if (JSON.stringify(nestedTables).length > 5) {
      return Promise.all(proms).then((x) => {
        return 1;
      });
    }
    console.log("asdadasdasdas");

    let nestedApiData = {};
    proms.push(
      new Promise((res, rej) => {
        ListCommanWay(`/admin/product/list`, {}, { paginate: 1000 })
          .then((x) => {
            let data = [];
            if (x.status == "SUCCESS") {
              data = x.data.data.map((d) => {
                return {
                  value: d.product_code,
                  label: d.product_name,
                };
              });
              nestedApiData["autoGen_product"] = data;
              productListRef.current = data;
              columnData.forEach((cd: any) => {
                if (cd.key == "autoGen_product") {
                  cd["options"] = data;
                }
              });
            }
            res(1);
          })
          .catch((c) => {
            res(0);
          });
      })
    );
    /*columnData.forEach((cd: any) => {
      if (cd.type == ADT.DB) {
        console.log("YESSS")
        proms.push(
          new Promise((res, rej) => {
            ListCommanWay(`/admin/${cd.db}/list`, {}, { paginate: 1000 })
              .then((x) => {
                let data = [];
                if (x.status == "SUCCESS") {
                  data = x.data.data.map((d) => {
                    console.log(cd?.keyVal, d[cd.keyVal], cd.keyVal)
                    return {
                      value: cd?.keyVal ? d[cd.keyVal] : d.id,
                      label: d[cd.db_col],
                    };
                  });
                  nestedApiData[cd.key] = data;
                  cd["options"] = data;
                }

                res(1);
              })
              .catch((c) => {
                res(0);
              });
          })
        );
      }
    });*/
    return Promise.all(proms).then((x) => {
      console.log(nestedApiData);
      nestedApiData["placeholder"] = [1, 2, 3, 4, 5];
      setNestedTables(nestedApiData);
      return 1;
    });
  };

  const modeActions = () => {
    if (mode) {
      console.log("data", data);

      if (mode === "edit") {
        mutateBooking(data.id);

        // Enable GST billing checkbox if no customer but billing_gst_no exists
        if (!data.customer_code && data.billing_gst_no) {
          setBillingDiffFromShipping(true);
        }

        Object.keys(data).map((obj) => {
          if (!["createdAt"].includes(obj)) {
            if (obj === "weight" && Number(data[obj]) >= 0) {
              setFieldValue(obj, Number(data[obj]));
            } else if (obj === "date") {
              console.log(
                "moment(new Date(data[obj])).format(YYYY-DD-MM)",
                data[obj],
                moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-DD-MM")
              );
              setFieldValue(obj, moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD"));
            } else {
              setFieldValue(obj, data[obj]);
            }
          }

          if (obj === "quantity" && Number(data[obj]) >= 0) {
            fetchDBInputData().then((value) => {
              value &&
                handleQuantityChange(
                  {
                    target: { value: data[obj] },
                  },
                  false
                );
            });
          }
        });
      } else if (mode === "view") {
        mutateBooking(data.id);

        Object.keys(data).map((obj) => {
          if (!["createdAt"].includes(obj)) {
            if (obj === "weight" && Number(data[obj]) >= 0) {
              setFieldValue(obj, Number(data[obj]));
            } else if (obj === "date") {
              setFieldValue(obj, moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD"));
            } else {
              setFieldValue(obj, data[obj]);
            }
          }

          if (obj === "quantity" && Number(data[obj]) >= 0) {
            fetchDBInputData().then((value) => {
              value &&
                handleQuantityChange(
                  {
                    target: { value: data[obj] },
                  },
                  false
                );
            });
          }
          if (obj === "weight" && Number(data[obj]) >= 0) {
            values[obj] = Number(data[obj]);
          }
          if (obj === "date") {
            values[obj] = moment(data[obj], "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD");
          }
        });
      }
    }
  };

  const [subbranchOptions, setSubBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const {
    error: subbranchError,
    isFetching: subbranchIsFetching,
    isError: subbranchIsError,
    isFetched: subbranchIsFetched,
    isLoading: subbranchIsLoading,
    refetch: subbranchRefetch,
  } = useQuery(["SubBranchesListApi"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt: any[] = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.id,
            label: p.branch_name,
          });
        });
        setSubBranchOptions(opt);
      }
    },
  });

  useEffect(() => {
    modeActions();
    handleQuantityChange(
      {
        target: { value: values.quantity },
      },
      false
    );
  }, []);

  console.log("-=======-----> newTableData", newTableData);

  const getInput: any = (type, label, value, name, isDisabled, width) => {
    let ip = <div></div>;

    switch (type) {
      case ADT.NUMBER:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className=" text-sm font-semibold text-gray-900">{label}</label>
            <input
              type="number"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              max={name == "quantity" ? 99 : 9999999}
              onChange={(e) => {
                if (Number(e.target.value) >= 0) {
                  handleChange(e);
                  if (name === "quantity") {
                    fetchDBInputData().then((value) => {
                      value && handleQuantityChange(e, false);
                    });
                  }
                }
              }}
              onWheel={handleWheel}
              disabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
            />
          </div>
        );
        break;
      case ADT.TEXT:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <input
              type="text"
              className={`border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1 ${
                isDisabled ? "bg-gray-300" : "bg-gray-50"
              }`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={async (e) => {
                handleBlur(e);
                if (label === "Pincode") {
                  setPincodeSection(e.target.name.split("_")[0]);
                  if (e.target.value.length == 6) {
                    const responseData = mutatePincode(e.target.value);
                  }
                }
                if (name === "customer_code" && !values.pickup_code) {
                  setCustomerWallet("N/A");
                  const responseData = mutateCustomer(e.target.value);
                }
                if (name === "pickup_code") {
                  const responseData = mutatePickup(e.target.value);
                }
                if (name == "shipper_mobile" && !values.pickup_code) {
                  mutateSavedMobile(e.target.value);
                }
              }}
              onChange={handleChange}
              disabled={
                ![
                  "shipper_sh",
                  "shipper_pincode",
                  "shipper_address",
                  "shipper_mobile",
                  "shipper_pan",
                  "shipper_gst_no",
                  "shipper_company",
                ].includes(name) &&
                mode &&
                (mode === "view" || mode === "edit")
                  ? true
                  : isDisabled
              }
            />
          </div>
        );
        break;
      case ADT.DB:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <Select
              className={`${isDisabled ? "bg-gray-300" : ""} bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full`}
              options={value.all}
              onBlur={handleBlur}
              onChange={(e) => {
                if (name === "branch_name") setIsBranchSet(e.value);
                setFieldValue(name, e);
              }}
              classNamePrefix="book_dd"
              name={name}
              value={values[name]}
              isDisabled={mode && (mode === "view" || mode === "edit") ? true : isDisabled}
              styles={{
                control: (base) => ({
                  ...base,
                  height: 30,
                  minHeight: 30,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  "& .book_dd__indicator": {
                    padding: "2px",
                  },
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
              }}
            />
          </div>
        );
        break;
      case ADT.TEXTAREA:
        break;
      case ADT.DATETIME:
        break;
      case ADT.DATE:
        ip = (
          <div className={`inline-flex items-center gap-2 ${width ? width : "w-auto"}`}>
            <label className="text-sm font-semibold text-gray-900 whitespace-nowrap">{label}</label>
            <input
              type="date"
              className={`${
                isDisabled ? "bg-gray-300" : ""
              } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1`}
              placeholder={label}
              name={name}
              value={values[name]}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={
                userDetails.roleName == roles.SUPER_ADMIN ? isDisabled : mode && (mode === "view" || mode === "edit") ? true : isDisabled
              }
              min={mode === "edit" ? undefined : moment().subtract(337, "days").format("YYYY-MM-DD")}
              max={mode === "edit" ? undefined : moment().format("YYYY-MM-DD")}
            />
          </div>
        );
        break;
      case ADT.FILE:
        break;
      default:
        ip = <></>;
        break;
    }
    return ip;
  };

  const extractWeight = (input) => {
    if (!input) return null;
    // Replace comma with dot (for decimal)
    let cleaned = input.replace(/,/g, ".");
    // Match number pattern: e.g. 2.5, .5, 5.
    const match = cleaned.match(/(\d+(\.\d+)?|\.\d+)/);
    return match ? parseFloat(match[0]) : null;
  };

  useEffect(() => {
    if (aiResponse.length) {
      doLoadAiData();
    }
  }, [aiResponse]);
  const doLoadAiData = () => {
    try {
      let aaa: any[] = [...aiResponse];

      let customer_code = aaa[0].fields[0].customer_code.replaceAll(" ", "");
      for (let index = 0; index < aaa.length; index++) {
        let aa = aaa[index].fields[0];
        if (customer_code != aa.customer_code.replaceAll(" ", "")) {
          setAiLoading(false);
          setAiReponse([]);
          alert("Customer Code mismatch");
          return;
        }
      }

      setFieldValue("quantity", aaa.length);
      handleQuantityChange({ target: { value: aaa.length } }, false);

      if (customer_code) {
        setCustomerWallet("N/A");
        setFieldValue("customer_code", customer_code);
        mutateCustomer(customer_code);
      }
      setTimeout(async () => {
        aaa[0].fields[0].shipper_name && setFieldValue("shipper_sh", aaa[0].fields[0].shipper_name);
        aaa[0].fields[0].shipper_addr && setFieldValue("shipper_address", aaa[0].fields[0].shipper_addr);
        setFieldValue("shipper_mobile", repairPhoneNumber(aaa[0].fields[0].shipper_mobile) || "0");

        let oldAwbData = [...newTableData];
        let retry = 0;
        // Wait until newTableData is available or retry 5 times
        while (!oldAwbData.length && retry < 10) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          oldAwbData = [...newTableDataRef.current];
          retry++;
        }

        if (oldAwbData.length) {
          oldAwbData.forEach((o, i) => {
            let ai_weight = extractWeight(aaa[i].fields[0]?.weight);

            if (ai_weight && !isNaN(ai_weight)) {
              o["weight"] = ai_weight;
            }

            if (aaa[i].fields[0]?.recv_pincode) {
              o["pincode"] = aaa[i].fields[0]?.recv_pincode;
              o["destination"] = aaa[i].fields[0]?.recv_dest;
            }

            o["awb_no"] = (aaa[i].fields[0]?.barcode || "").replaceAll(" ", "").toUpperCase();
            o["contact_no"] = repairPhoneNumber(aaa[i].fields[0].recv_mobile.replaceAll(" ", ""));
            o["consignee"] = aaa[i].fields[0].recv_name;
            o["address"] = aaa[i].fields[0].recv_addr;
          });
          console.log(oldAwbData);
          setNewTableData(oldAwbData);
          setAiLoading(false);
          setAiReponse([]);
        } else {
          setAiLoading(false);
          setAiReponse([]);
          alert("Do Manual Booking");
        }
      }, 200);
    } catch (c) {
      setAiLoading(false);
      setAiReponse([]);
      alert("Do manual booking");
    }
  };

  const handleLubiResponse = async (data: any[]) => {
    try {
      // Quantity is always 1 for this customer
      setFieldValue("quantity", 1);

      // Check Auto Generate
      setFieldValue("autoGen", 1);

      // Load product list before creating rows
      await fetchDBInputData();
      handleQuantityChange({ target: { value: 1 } }, false);

      const first = data[0];

      // Warn if multiple images have different eWaybillNo
      if (data.length > 1) {
        const eWaybills = data.map((item) => item.eWaybillNo).filter(Boolean);
        const uniqueEWaybills = [...new Set(eWaybills)];
        if (uniqueEWaybills.length > 1) {
          alert("Warning: Different E-Waybill numbers detected across invoices: " + uniqueEWaybills.join(", "));
        }
      }

      // E-Waybill (use first one)
      first.eWaybillNo && setFieldValue("ewayBillNo", first.eWaybillNo);

      // Sum of all invoiceTotal for commercialValue
      const totalCommercialValue = data.reduce((sum, item) => sum + (Number(item.invoiceTotal) || 0), 0);

      // Extract pincode from address (format: "...,India-XXXXXX")
      const pincodeMatch = (first.shippingAddress || "").match(/(\d{6})\s*$/);

      // Fetch destination from pincode
      let destination = "";
      if (pincodeMatch) {
        try {
          const pincodeRes: any = await GetCommanWay("/admin/address/" + pincodeMatch[1], {}, {});
          if (pincodeRes?.status === "SUCCESS" && pincodeRes?.data?.city) {
            destination = pincodeRes.data.city;
          }
        } catch (e) {
          console.error("Pincode lookup failed:", e);
        }
      }

      // Wait for table data to be ready
      let oldAwbData: any[] = [];
      let retry = 0;
      while (!oldAwbData.length && retry < 10) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        oldAwbData = [...newTableDataRef.current];
        retry++;
      }

      if (oldAwbData.length) {
        const row = oldAwbData[0];

        // Consignee info from shipping
        row["consignee"] = first.shippingAddressRecipient || "";
        row["address"] = first.shippingAddress || "";
        row["contact_no"] = first.shipTo?.phone || "";
        if (pincodeMatch) row["pincode"] = pincodeMatch[1];
        if (destination) row["destination"] = destination;

        // Commercial value = sum of all invoiceTotals
        row["commercialValue"] = totalCommercialValue || "";

        // Build pieces from each item in response (1 piece per file/invoice)
        row["piece"] = data.map((item: any) => ({
          l: "",
          b: "",
          h: "",
          weight: "",
          do_no: item.doNo || "",
        }));

        console.log("Lubi processed data:", oldAwbData);
        setNewTableData(oldAwbData);
      } else {
        alert("Do Manual Booking");
      }
    } catch (err) {
      console.error("Lubi response processing error:", err);
      alert("Do manual booking");
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1">
      {(isupdateBookingloading || isloading || aiLoading) && <Spinner />}

      {isLabelOpen != -1 && (
        <AwbLabel
          isLabelOpen={isLabelOpen == -1 ? false : true}
          setIsLabelOpen={(e) => {
            setIsLabelOpen(e ? 0 : -1);
          }}
          awbData={savedAwbData[isLabelOpen]}
          setAwbData={() => {}}
        />
      )}

      <MultiImageUploadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        setAiReponse={setAiReponse}
        setAiLoading={setAiLoading}
        aiLoading={aiLoading}
        customer_code={values.customer_code}
        onLubiResponse={handleLubiResponse}
      />

      {/* ═══════════ HEADER BAR ═══════════ */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1"></div>
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">Add Domestic Booking</h1>
            <div className="flex items-center justify-end gap-2 flex-1">
              <Button
                size="sm"
                color="primary"
                variant="flat"
                className="h-7 text-xs"
                onPress={() => setModalOpen(true)}
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                EXA Detect
              </Button>
              {mode && mode === "edit" && (
                <Button color="warning" size="sm" variant="flat" className="h-7 text-xs" onPress={() => navigate(-1)}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        {/* ═══════════ BOOKING FORM - FULL WIDTH ═══════════ */}
        <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl mb-2">
          <div className="pb-0 pt-2 px-3">
            <div className="flex items-center justify-between gap-2 w-full flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h2 className="text-sm font-bold text-slate-800">Booking Form</h2>
                <span className="text-slate-300 mx-1">|</span>
                <MCheckbox
                  label="Auto Generate"
                  name="autoGen"
                  value={values.autoGen}
                  onChange={(e: any) => {
                    setFieldValue("autoGen", e.target.checked ? 1 : 0);
                    if (e.target.checked && values.quantity > 0) {
                    }
                  }}
                />
                {userDetails?.roleName != "CUSTOMER" && (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-slate-500">Type:</span>
                    <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all text-[12px] font-medium ${values.type === "PU" ? "border-primary2 bg-primary2/10 text-primary2" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                      <input
                        type="radio"
                        value="PU"
                        name="type"
                        checked={values.type === "PU"}
                        onChange={(e) => setFieldValue("type", e.target.value)}
                        disabled={!!(mode && (mode === "view" || mode === "edit"))}
                        className="w-3.5 h-3.5 text-primary2 focus:ring-primary2/20"
                      />
                      P/U
                    </label>
                    <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all text-[12px] font-medium ${values.type === "WI" ? "border-primary2 bg-primary2/10 text-primary2" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                      <input
                        type="radio"
                        value="WI"
                        name="type"
                        checked={values.type === "WI"}
                        onChange={(e) => {
                          setFieldValue("type", e.target.value);
                          setPickupProductInfo(null);
                        }}
                        disabled={!!(mode && (mode === "view" || mode === "edit"))}
                        className="w-3.5 h-3.5 text-primary2 focus:ring-primary2/20"
                      />
                      W/I
                    </label>
                    {values.customer_code === "AHD48277" && (
                      <MInput
                        label="Main AWB"
                        name="mainAwb"
                        value={mainAwb}
                        onChange={(e: any) => setMainAwb(e.target.value)}
                        onBlur={() => {}}
                        disabled={mode === "view"}
                        className="min-w-[160px]"
                        placeholder="Main Airwaybill"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.keys(userDetails).length &&
                (userDetails?.roleName === roles.SUPER_ADMIN || userDetails?.roleName === roles.BRANCH_ADMIN) ? (
                  <MSelect
                    label=""
                    name="subbranch"
                    options={subbranchOptions}
                    value={values.subbranch}
                    onChange={(e: any) => setFieldValue("subbranch", e)}
                    onBlur={handleBlur}
                    className="min-w-[140px]"
                  />
                ) : null}
              </div>
            </div>
          </div>
          <hr className="mt-1.5 border-slate-200" />
          <div className="px-3 py-2">
            <div className="grid grid-cols-6 gap-x-3 gap-y-1">
              {values.type === "PU" && (
                <MInput
                  label="Ref Code"
                  name="pickup_code"
                  value={values.pickup_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onBlurExtra={(e: any) => mutatePickup(e.target.value)}
                  disabled={mode && (mode === "view" || mode === "edit")}
                />
              )}
              <div className="flex items-center gap-1.5">
                <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">Date</label>
                <input
                  type="date"
                  className="flex-1 min-w-0 px-2 py-[3px] rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
                  name="date"
                  value={values.date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={userDetails.roleName == roles.SUPER_ADMIN ? false : !!(mode && (mode === "view" || mode === "edit"))}
                  min={mode === "edit" ? undefined : moment().subtract(337, "days").format("YYYY-MM-DD")}
                  max={mode === "edit" ? undefined : moment().format("YYYY-MM-DD")}
                />
              </div>
              <MInput
                label="Qty"
                name="quantity"
                type="number"
                value={values.quantity}
                onChange={(e: any) => {
                  if (Number(e.target.value) >= 0) {
                    handleChange(e);
                    fetchDBInputData().then((value) => {
                      value && handleQuantityChange(e, false);
                    });
                  }
                }}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit")}
                max={99}
              />
              <MInput
                label="Remarks"
                name="remarks"
                value={values.remarks}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={mode && (mode === "view" || mode === "edit")}
              />
              {userDetails?.roleName !== "CUSTOMER" && (
                <MInput
                  label="E-way Bill"
                  name="ewayBillNo"
                  value={values.ewayBillNo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={mode && mode === "view"}
                />
              )}
              {userDetails?.roleName !== "CUSTOMER" && (
                <MCheckbox
                  label="GST Billing"
                  name="billingDiffFromShipping"
                  value={billingDiffFromShipping}
                  onChange={(e: any) => {
                    setBillingDiffFromShipping(e.target.checked);
                    if (e.target.checked) {
                      // Checked: switching to GST billing mode, clear customer code
                      setFieldValue("customer_code", "");
                      setFieldValue("customer_sh", "");
                      setFieldValue("customer_pincode", "");
                      setFieldValue("customer_city", "");
                      setFieldValue("customer_state", "");
                      setFieldValue("customer_country", "");
                      setFieldValue("customer_address", "");
                      setFieldValue("customer_mobile", "");
                      setCustomerWallet("N/A");
                    } else {
                      // Unchecked: normal mode, clear billing fields
                      setFieldValue("billing_name", "");
                      setFieldValue("billing_address", "");
                      setFieldValue("billing_city", "");
                      setFieldValue("billing_state", "");
                      setFieldValue("billing_pincode", "");
                      setFieldValue("billing_country", "India");
                      setFieldValue("billing_gst_no", "");
                      setFieldValue("billing_mobile", "");
                    }
                  }}
                  disabled={mode === "view"}
                />
              )}
            </div>
          </div>
        </div>

        {/* ═══════════ CUSTOMER & SHIPPER - SIDE BY SIDE ═══════════ */}
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
          {/* Customer Info Card - LEFT (shown when billingDiffFromShipping is false = normal mode) */}
          {userDetails?.roleName != "CUSTOMER" && !billingDiffFromShipping && (
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">CUSTOMER</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="flex items-center justify-between mb-1">
                      <MInput
                        label="Code"
                        name="customer_code"
                        value={values.customer_code}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onBlurExtra={(e: any) => {
                          if (!values.pickup_code) {
                            setCustomerWallet("N/A");
                            mutateCustomer(e.target.value);
                          }
                        }}
                        disabled={values.pickup_code ? true : false}
                        className="flex-1 max-w-[180px]"
                      />
                      {customerWallet != "N/A" && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg ${Number(customerWallet) < 500 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
                          Wallet: ₹{customerWallet}
                        </span>
                      )}
                    </div>
                    <div className="hidden">
                      <MInput label="Mobile" name="customer_mobile" value={values.customer_mobile} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="Country" name="customer_country" value={values.customer_country} onChange={handleChange} onBlur={handleBlur} disabled />
                    </div>
                    <MInput label="S/H" name="customer_sh" value={values.customer_sh} onChange={handleChange} onBlur={handleBlur} disabled />
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput label="Pincode" name="customer_pincode" value={values.customer_pincode} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="City" name="customer_city" value={values.customer_city} onChange={handleChange} onBlur={handleBlur} disabled />
                      <MInput label="State" name="customer_state" value={values.customer_state} onChange={handleChange} onBlur={handleBlur} disabled />
                    </div>
                    <MInput label="Address" name="customer_address" value={values.customer_address} onChange={handleChange} onBlur={handleBlur} disabled />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Info Card - LEFT (shown when billingDiffFromShipping is true = GST billing) */}
          {userDetails?.roleName != "CUSTOMER" && billingDiffFromShipping && (
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="p-0">
                <div className="flex">
                  <div className="w-7 flex-shrink-0 bg-amber-500/70 rounded-l-xl flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">BILLING</span>
                  </div>
                  <div className="flex-1 px-2.5 py-2 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <MInput label="GST No" name="billing_gst_no" value={values.billing_gst_no} onChange={handleChange} onBlur={handleBlur} onBlurExtra={(e: any) => handleGstSearch(e.target.value, setFieldValue)} className="flex-1" />
                      {gstSearching && <span className="text-[10px] text-blue-500 animate-pulse">Searching...</span>}
                    </div>
                    <MInput label="Name" name="billing_name" value={values.billing_name} onChange={handleChange} onBlur={handleBlur} />
                    <MInput label="Mobile" name="billing_mobile" type="number" value={values.billing_mobile} onChange={handleChange} onBlur={handleBlur} />
                    <MInput label="Address" name="billing_address" value={values.billing_address} onChange={handleChange} onBlur={handleBlur} />
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput label="Pincode" name="billing_pincode" value={values.billing_pincode} onChange={handleChange} onBlur={handleBlur} />
                      <MInput label="City" name="billing_city" value={values.billing_city} onChange={handleChange} onBlur={handleBlur} />
                      <MInput label="State" name="billing_state" value={values.billing_state} onChange={handleChange} onBlur={handleBlur} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shipper Info Card - RIGHT */}
          <div className={`shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl ${userDetails?.roleName == "CUSTOMER" ? "col-span-2" : ""}`}>
            <div className="p-0">
              <div className="flex">
                <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SHIPPER</span>
                </div>
                <div className="flex-1 px-2.5 py-2 space-y-1">
                  <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                    <MInput
                      label="Mobile"
                      name="shipper_mobile"
                      value={values.shipper_mobile}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onBlurExtra={(e: any) => {
                        if (!values.pickup_code) mutateSavedMobile(e.target.value);
                      }}
                      disabled={mode === "view" || mode === "edit" ? !["shipper_mobile"].includes("shipper_mobile") : false}
                    />
                    <MInput
                      label="Pincode"
                      name="shipper_pincode"
                      value={values.shipper_pincode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onBlurExtra={(e: any) => {
                        setPincodeSection("shipper");
                        if (e.target.value.length == 6) mutatePincode(e.target.value);
                      }}
                      disabled={userDetails?.roleName == "CUSTOMER"}
                    />
                    <MInput label="S/H" name="shipper_sh" value={values.shipper_sh} onChange={handleChange} onBlur={handleBlur} />
                  </div>
                  <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                    <MInput label="City" name="shipper_city" value={values.shipper_city} onChange={handleChange} onBlur={handleBlur} disabled />
                    <MInput label="State" name="shipper_state" value={values.shipper_state} onChange={handleChange} onBlur={handleBlur} disabled />
                    <MInput label="Country" name="shipper_country" value={values.shipper_country} onChange={handleChange} onBlur={handleBlur} disabled />
                  </div>
                  <MInput label="Company" name="shipper_company" value={values.shipper_company} onChange={handleChange} onBlur={handleBlur} disabled={userDetails?.roleName == "CUSTOMER"} />
                  <MInput label="Address" name="shipper_address" value={values.shipper_address} onChange={handleChange} onBlur={handleBlur} disabled={userDetails?.roleName == "CUSTOMER"} />
                  <div className="hidden">
                    <MCheckbox
                      label="Change Shipper"
                      name="change_shipper"
                      value={values.change_shipper}
                      onChange={(e: any) => setFieldValue("change_shipper", e.target.checked)}
                      disabled={mode && (mode === "view" || mode === "edit")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ COMMENT & SAVE BUTTON ═══════════ */}
        <div className="shadow-xl shadow-slate-200/50 bg-slate-100/80 backdrop-blur-sm overflow-visible rounded-xl px-3 py-2 mt-2">
          <div className="flex items-center justify-end gap-3 flex-wrap">
            {mode === "edit" && (
              <MInput
                label="Comment"
                name="commentVal"
                value={commentVal}
                onChange={(e: any) => setCommentVal(e.target.value)}
                placeholder="Required"
                className="flex-1 max-w-[300px]"
              />
            )}
            {mode && mode === "view" ? (
              <Button
                size="sm"
                color="secondary"
                variant="solid"
                className="h-8 text-xs font-semibold"
                onPress={() => navigate(-1)}
              >
                Go Back
              </Button>
            ) : (
              <Button
                type="submit"
                size="sm"
                className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-lg shadow-lg shadow-[#001f3f]/30 h-8 text-xs tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all px-6"
                isLoading={isSubmitting}
              >
                {mode === "edit" ? "✏️ Update Booking" : "🚀 Save Booking"}
              </Button>
            )}
          </div>
        </div>

        {/* ═══════════ AWB DATA TABLE ═══════════ */}
        <div className="mt-3">
          <div className="shadow-xl shadow-slate-200/50 bg-white/80 rounded-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/80">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-slate-800">AWB Data</span>
                <span className="text-[10px] text-slate-500">({newTableData.length} items)</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <BookingInputTable
                nestedTables={nestedTables}
                customer_code={values.customer_code}
                columnData={columnData}
                tableData={newTableData}
                setNewTableData={setNewTableData}
                customClasses="flex-1"
                mode={mode}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddBooking;
function repairPhoneNumber(input) {
  if (!input) return "";

  // Remove all non-digit characters
  let cleaned = input.replace(/\D/g, "");

  // Remove leading 0s or country code 91 if present
  if (cleaned.startsWith("91") && cleaned.length > 10) {
    cleaned = cleaned.slice(2);
  } else if (cleaned.startsWith("0") && cleaned.length > 10) {
    cleaned = cleaned.slice(1);
  }

  return cleaned;
  // Final check: valid mobile number must be 10 digits and start with 6-9
  if (/^[6-9]\d{9}$/.test(cleaned)) {
    return cleaned;
  }

  return "Invalid Number";
}
